import { ArrowLeft, CalendarClock, Trash2, User as UserIcon } from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useParams } from 'react-router-dom'
import { RelativeTime } from '@/components/custom-ui/RelativeTime'
import { InvestigationActivityCard } from '@/components/investigations/InvestigationActivityCard'
import { InvestigationDescriptionCard } from '@/components/investigations/InvestigationDescriptionCard'
import { InvestigationDetailHeading } from '@/components/investigations/InvestigationDetailHeading'
import {
  InvestigationDetailError,
  InvestigationDetailLoader,
  InvestigationDetailNotFound,
} from '@/components/investigations/InvestigationDetailStates'
import { InvestigationSourcePill } from '@/components/investigations/InvestigationDossier'
import { InvestigationMetaTile } from '@/components/investigations/InvestigationMetaTile'
import { InvestigationNoteComposer } from '@/components/investigations/InvestigationNoteComposer'
import { InvestigationNotesCard } from '@/components/investigations/InvestigationNotesCard'
import { InvestigationObjectsCard } from '@/components/investigations/InvestigationObjectsCard'
import { InvestigationReportCard } from '@/components/investigations/InvestigationReportCard'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { InvestigationHypothesisChip } from '@/features/analytic-hypotheses/InvestigationHypothesisChip'
import { useInvestigationActions } from '@/features/investigations/hooks/useInvestigationActions'
import { useInvestigationDescriptionEditor } from '@/features/investigations/hooks/useInvestigationDescriptionEditor'
import { useInvestigationNoteComposer } from '@/features/investigations/hooks/useInvestigationNoteComposer'
import { useInvestigationTimeline } from '@/features/investigations/hooks/useInvestigationTimeline'
import { ResearchSnapshotPanel } from '@/features/investigations/ResearchSnapshotPanel'
import {
  useInvestigationsStore,
  useInvestigationsStoreApi,
} from '@/stores/investigations-store-provider'
import type { Investigation } from '@/types/investigation'
import { formatDateTime } from '@/utils/date'
import { useCaseContext } from '../CaseContext'

/**
 * Detail page for a single saved investigation. On mount we mark it as the
 * case's `activeId` and do NOT clear it on unmount — the chosen investigation
 * stays the "current viewpoint" as the user moves between Map / Graph / Timeline.
 */
export default function CaseInvestigationDetailPage() {
  const { caseId } = useCaseContext()
  const { investigationId } = useParams<{ investigationId: string }>()
  const saved = useInvestigationsStore(s => s.saved)
  const isLoading = useInvestigationsStore(s => s.isLoading)
  const error = useInvestigationsStore(s => s.error)
  const storeApi = useInvestigationsStoreApi()

  useEffect(() => {
    if (!investigationId) return
    storeApi.getState().setActive(investigationId)
  }, [investigationId, storeApi])

  if (!investigationId) return <Navigate to={`/cases/${caseId}/investigations`} replace />

  const backHref = `/cases/${caseId}/investigations`
  const investigation = saved.find(inv => inv.id === investigationId)
  const showLoading = isLoading && !investigation
  const showError = !!error && !investigation
  const showNotFound = !showLoading && !showError && !investigation && saved.length > 0

  if (showLoading) return <InvestigationDetailLoader />
  if (showError)
    return (
      <InvestigationDetailError
        backHref={backHref}
        onRetry={() => storeApi.getState().fetchAll()}
      />
    )
  if (showNotFound) return <InvestigationDetailNotFound backHref={backHref} />
  if (!investigation) return <InvestigationDetailLoader />

  return <DetailBody inv={investigation} caseId={caseId} />
}

function DetailBody({ inv, caseId }: { inv: Investigation; caseId: string }) {
  const { t } = useTranslation()
  const dirty = useInvestigationsStore(s => s.dirty && s.activeId === inv.id)
  const actions = useInvestigationActions(inv, caseId)
  const description = useInvestigationDescriptionEditor(inv)
  const timeline = useInvestigationTimeline(inv)
  const noteComposer = useInvestigationNoteComposer(inv)

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <div className="flex flex-1 min-h-0 flex-col gap-4 overflow-y-auto px-4 pt-6 pb-8 md:px-8">
        <div>
          <Button asChild variant="ghost" size="sm" className="-ml-2">
            <Link to={`/cases/${caseId}/investigations`}>
              <ArrowLeft className="size-3.5" />
              {t('investigations.detail.back')}
            </Link>
          </Button>
        </div>

        <InvestigationDetailHeading
          inv={inv}
          dirty={dirty}
          togglingReport={actions.togglingReport}
          onOpenInGraph={actions.openInGraph}
          onRequestDelete={() => actions.setConfirmDelete(true)}
          onToggleReport={actions.toggleReport}
        />

        <InvestigationHypothesisChip caseId={caseId} hypothesisRef={inv.hypothesisRef} />

        <div className="grid grid-cols-2 gap-3 md:grid-cols-1 md:gap-4 lg:grid-cols-3 max-md:[&>*:first-child]:col-span-2">
          <InvestigationMetaTile
            icon={<UserIcon className="size-4" />}
            label={t('investigations.detail.meta.author')}
            primary={inv.authorName}
            secondary={<InvestigationSourcePill inv={inv} />}
          />
          <InvestigationMetaTile
            icon={<CalendarClock className="size-4" />}
            label={t('investigations.detail.meta.created')}
            primary={<RelativeTime at={inv.createdAt} />}
            secondary={<span className="font-mono text-2xs">{formatDateTime(inv.createdAt)}</span>}
          />
          <InvestigationMetaTile
            icon={<CalendarClock className="size-4" />}
            label={t('investigations.detail.meta.updated')}
            primary={<RelativeTime at={inv.updatedAt} />}
            secondary={<span className="font-mono text-2xs">{formatDateTime(inv.updatedAt)}</span>}
          />
        </div>

        <InvestigationDescriptionCard
          draft={description.draft}
          onDraftChange={description.setDraft}
          saving={description.saving}
          isDirty={description.isDirty}
          disabled={description.disabled}
          onSave={description.save}
          onReset={description.reset}
        />

        {/* A saved research report (PRD-0076) is its own narrative and sources; the
            snapshot panel (and its regenerate) is for everything else. */}
        {inv.reportMd ? <InvestigationReportCard inv={inv} /> : <ResearchSnapshotPanel inv={inv} />}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,_1.4fr)_minmax(0,_1fr)]">
          <InvestigationNotesCard
            timeline={timeline}
            composer={
              <InvestigationNoteComposer
                onAdd={noteComposer.onAdd}
                disabled={noteComposer.disabled}
              />
            }
          />
          <InvestigationObjectsCard inv={inv} />
        </div>

        <InvestigationActivityCard inv={inv} />
      </div>

      <AlertDialog
        open={actions.confirmDelete}
        onOpenChange={open => {
          if (!open && !actions.deleting) actions.setConfirmDelete(false)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="size-4 text-destructive" />
              {t('investigations.page.delete.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('investigations.page.delete.description', { name: inv.title })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actions.deleting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={actions.confirmDeleteAction}
              disabled={actions.deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {actions.deleting
                ? t('investigations.page.delete.deleting')
                : t('investigations.page.delete.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
